'use client';
/**
 * @file     index.tsx
 * @purpose  Support page for the Relta Agent app: FAQs + contact form.
 */
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { SupportForm } from './SupportForm';

const ReltaAgentSupportView = () => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();

  const faqs = [
    {
      q: t('pages.reltaAgentSupport.faq.offline.q', {
        defaultValue: 'Does the app work without internet at an open house?',
      }),
      a: t('pages.reltaAgentSupport.faq.offline.a', {
        defaultValue:
          'Yes. Relta Agent collects visitor sign-ins offline and syncs every response automatically once you are back online.',
      }),
    },
    {
      q: t('pages.reltaAgentSupport.faq.crm.q', {
        defaultValue: 'Which CRMs can I sync to?',
      }),
      a: t('pages.reltaAgentSupport.faq.crm.a', {
        defaultValue:
          'On a paid plan, each intake is pushed to Follow Up Boss, BoldTrail, Pipedrive or HubSpot automatically. Lofty is coming soon. You can also export to CSV.',
      }),
    },
    {
      q: t('pages.reltaAgentSupport.faq.trial.q', {
        defaultValue: 'How does the free trial work?',
      }),
      a: t('pages.reltaAgentSupport.faq.trial.a', {
        defaultValue:
          'Every plan starts with a 14-day free trial. Choose monthly or annual billing, and nothing is charged until the trial ends.',
      }),
    },
    {
      q: t('pages.reltaAgentSupport.faq.team.q', {
        defaultValue: 'Can my whole team use it?',
      }),
      a: t('pages.reltaAgentSupport.faq.team.a', {
        defaultValue:
          'Yes. Solo covers one agent, Team covers up to 3, and Team Plus covers 4 or more.',
      }),
    },
  ];

  return (
    <Box sx={{ backgroundColor: theme.semanticColors.backgroundPrimary }}>
      {/* Header */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 11 },
          px: 2,
          background: `linear-gradient(180deg, ${theme.semanticColors.backgroundSecondary} 0%, ${theme.semanticColors.backgroundPrimary} 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: theme.typography.bodySm,
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: theme.semanticColors.interactivePrimary,
                mb: 2,
              }}
            >
              {t('pages.reltaAgentSupport.header.eyebrow', { defaultValue: 'Relta Agent support' })}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: theme.typography.h2, md: theme.typography.h1 },
                fontWeight: 700,
                lineHeight: 1.1,
                color: theme.semanticColors.textPrimary,
                mb: 2.5,
              }}
            >
              {t('pages.reltaAgentSupport.header.title', { defaultValue: 'Get help with Relta Agent' })}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: theme.typography.bodyMd, md: theme.typography.bodyLg },
                color: theme.semanticColors.textSecondary,
                lineHeight: 1.6,
                maxWidth: 560,
                mx: 'auto',
              }}
            >
              {t('pages.reltaAgentSupport.header.subhead', {
                defaultValue:
                  'Check the common questions below, or send a message and you will get a reply by email.',
              })}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* FAQs */}
      <Box component="section" sx={{ py: { xs: 6, md: 9 }, px: 2 }}>
        <Container maxWidth="md">
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: theme.typography.h3, md: theme.typography.h2 },
              fontWeight: 700,
              color: theme.semanticColors.textPrimary,
              mb: 4,
              textAlign: 'center',
            }}
          >
            {t('pages.reltaAgentSupport.faq.title', { defaultValue: 'Common questions' })}
          </Typography>
          <Stack spacing={2.5}>
            {faqs.map((faq, i) => (
              <Box
                key={i}
                sx={{
                  p: 3,
                  borderRadius: 1,
                  backgroundColor: theme.semanticColors.surfaceCard,
                  border: `1px solid ${theme.semanticColors.borderSubtle}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: theme.typography.bodyLg,
                    fontWeight: 600,
                    color: theme.semanticColors.textPrimary,
                    mb: 1,
                  }}
                >
                  {faq.q}
                </Typography>
                <Typography
                  sx={{
                    fontSize: theme.typography.bodyMd,
                    color: theme.semanticColors.textSecondary,
                    lineHeight: 1.6,
                  }}
                >
                  {faq.a}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Contact form */}
      <Box
        component="section"
        sx={{ py: { xs: 6, md: 9 }, px: 2, backgroundColor: theme.semanticColors.backgroundSecondary }}
      >
        <Container maxWidth="sm">
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: theme.typography.h3, md: theme.typography.h2 },
              fontWeight: 700,
              color: theme.semanticColors.textPrimary,
              mb: 1.5,
              textAlign: 'center',
            }}
          >
            {t('pages.reltaAgentSupport.contact.title', { defaultValue: 'Send a message' })}
          </Typography>
          <Typography
            sx={{
              fontSize: theme.typography.bodyMd,
              color: theme.semanticColors.textSecondary,
              mb: 4,
              textAlign: 'center',
            }}
          >
            {t('pages.reltaAgentSupport.contact.subhead', {
              defaultValue: 'Or email support@relta.ca directly.',
            })}
          </Typography>
          <SupportForm />
        </Container>
      </Box>
    </Box>
  );
};

export default ReltaAgentSupportView;
